import Card from "components/card";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { IoEye } from "react-icons/io5";
import Pagination from "components/pagination/Pagination";
import Modal from "components/modal/Modal";
import EventsModal from "./EventsModal";
import EventService from "services/events.service";
import { FiSearch } from "react-icons/fi";
import Avatar from "react-avatar";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import {  FaTrash } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { AiFillEdit } from "react-icons/ai";
import CreateEventPosts from "../CreateEventPosts";

import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import tabCache from "utils/tabCache";

const columnHelper = createColumnHelper();

const Events = () => {
  const [searchParams] = useSearchParams();
  const startDateParam = searchParams.get("startDate");
  const endDateParam = searchParams.get("endDate");

  const dateRangeFromParams = React.useMemo(() => {
    if (!startDateParam || !endDateParam) {
      return null;
    }

    const start = new Date(startDateParam);
    const end = new Date(endDateParam);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }

    const normalizedStart = new Date(start);
    normalizedStart.setHours(0, 0, 0, 0);

    const normalizedEnd = new Date(end);
    normalizedEnd.setHours(23, 59, 59, 999);

    return {
      startDate: normalizedStart.toISOString(),
      endDate: normalizedEnd.toISOString(),
    };
  }, [startDateParam, endDateParam]);
  const activateRef = useRef();
  const prevSearchTextRef = useRef("");
  const [sorting, setSorting] = React.useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(3);
  // Initialize from cache - show loader only on first load
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [viewOpen, setViewOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [id, setId] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [eventStatus, setEventStatus] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [popupOpenDelete, setPopupOpenDelete] = useState(false);
  const [eventData, setEventData] = useState([]);
  const [editingLikeCount, setEditingLikeCount] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [isUpdatingLikeCount, setIsUpdatingLikeCount] = useState(false);

  const [isPageChanging, setIsPageChanging] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);
  const [isSoldTickets, setIsSoldTickets] = useState(false);



  const colourStyles = {
    control: (styles, state) => ({
      ...styles,
      backgroundColor: "rgb(30 30 32 / 1)",
      border: "1px solid rgba(255, 255, 255, 0.1) !important",
      boxShadow: "none !important",
      "&:hover": {
        border: "1px solid rgba(255, 255, 255, 0.1) !important",
      },
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "#ffffff",
    }),
    option: (styles) => ({
      ...styles,
      backgroundColor: "rgb(30 30 32 / 1) !important",
      ":hover": {
        backgroundColor: "rgba(255, 255, 255, 0.30) !important",
        color: "#ffffff",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: "rgb(12 14 28 / 1)",
      borderRadius: "12px",
      border: "1px solid #2A2A2A",
      overflow: "hidden",
      marginTop: "10px",
    }),
  };

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  const handleOpenDelete = async (id, active, data) => {
    setSelectedUser(data);
    setId(id);
    setIsSoldTickets(!!data?.isSoldTickets); 
    setPopupOpenDelete(true);
    document.body.style.overflow = "hidden";
  };

  const handleDelete = async () => {
    setConfirmLoader(true);
    try {
      const body = {
        post_event_id: id,
      };
      const response = await EventService.deleteEvent(body);
      setPopupOpenDelete(false);
      document.body.style.overflow = "unset";
      getEvents();
      Toast("success", response.data.message);
    } catch (error) {
      Toast("error", error.response.data.message);
    } finally {
      setConfirmLoader(false);
    }
  };

  const handleEditLikeCount = (rowId, currentValue) => {
    setEditingLikeCount(rowId);
    setEditingValue(currentValue.toString());
  };

  const handleSaveLikeCount = async (rowId) => {
    if (editingValue.trim() === "") {
      Toast("error", "Like count cannot be empty");
      return;
    }

    const numericValue = parseInt(editingValue, 10);
    if (isNaN(numericValue) || numericValue < 0) {
      Toast("error", "Please enter a valid non-negative number");
      return;
    }

    setIsUpdatingLikeCount(true);
    try {
      const body = {
        post_event_id: rowId,
        likecount: numericValue,
      };
      const response = await EventService.updateLikeCount(body);
      Toast("success", response.data.message);
      setEditingLikeCount(null);
      setEditingValue("");
      getEvents(); // Refresh the data
    } catch (error) {
      Toast("error", error.response?.data?.message || "Failed to update like count");
    } finally {
      setIsUpdatingLikeCount(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingLikeCount(null);
    setEditingValue("");
  };

  const handleLikeCountKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, and numbers
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow numbers
        (e.keyCode >= 48 && e.keyCode <= 57) ||
        // Allow numpad numbers
        (e.keyCode >= 96 && e.keyCode <= 105)) {
      return;
    }
    // Prevent any other key
    e.preventDefault();
  };

  const columns = [
    columnHelper.accessor("images", {
      id: "images",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white">
          Title
        </p>
      ),

      cell: (info) => {
        const row = info.row.original;
        const title = row?.title || "--";

        const rawUsername =
          typeof row?.accountDetails?.username === "string"
            ? row.accountDetails.username.trim()
            : "";
        const rawName =
          typeof row?.accountDetails?.name === "string"
            ? row.accountDetails.name.trim()
            : "";
        const displayName = rawUsername || rawName || "--";

        const avatarFallbackName =
          displayName !== "--" ? displayName : title || "Event";

        return (
          <div className="flex items-center gap-3">
            {info.getValue()?.[0] ? (
              <img
                src={
                  info.getValue()?.[0]?.includes("http")
                    ? info.getValue()?.[0]
                    : process.env.REACT_APP_BASE_URL + info.getValue()?.[0]
                }
                alt="Profile"
                className="!h-8 !w-8 rounded-lg object-cover"
              />
            ) : (
              <Avatar
                name={avatarFallbackName}
                className="!h-8 !w-8 rounded-lg object-cover"
              />
            )}
            <div className="flex flex-col">
              <p className="!line-clamp-2 font-medium text-white">{title}</p>
              <span className="text-xs text-gray-600">{displayName}</span>
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white pl-10">
          Description
        </p>
      ),

      cell: (info) => (
        <div className="line-clamp-2 w-[200px] items-center overflow-hidden text-ellipsis whitespace-normal ml-10">
          {info.getValue() ? info.getValue() : "--"}
          {/* <Progress width="w-[108px]" value={info.getValue()} /> */}
        </div>
      ),
    }),
    columnHelper.accessor("city", {
      id: "city",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white">City</p>
      ),

      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("state", {
      id: "state",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white">
          State
        </p>
      ),

      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("country", {
      id: "country",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white">
          Country
        </p>
      ),

      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() ? info.getValue() : "--"}
        </div>
      ),
    }),
    columnHelper.accessor("likecount", {
      id: "likecount",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white">
          Likes
        </p>
      ),

      cell: (info) => {
        const rowId = info.row.original._id;
        const currentValue = info.getValue() || 0;
        const isEditing = editingLikeCount === rowId;

        return (
          <div className="flex w-[150px] items-center gap-2">
            {isEditing ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={(e) => {
                    handleLikeCountKeyDown(e);
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveLikeCount(rowId);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelEdit();
                    }
                  }}
                  className="flex h-8 w-20 items-center justify-center rounded-xl border border-white/10 bg-white/0 px-2 text-sm text-white outline-none placeholder:text-gray-600"
                  autoFocus
                  placeholder="0"
                />
                <button
                  onClick={() => handleSaveLikeCount(rowId)}
                  disabled={isUpdatingLikeCount}
                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#38B600] text-white hover:bg-green-600 disabled:opacity-50"
                >
                  {isUpdatingLikeCount ? (
                    <img src={Loader} alt="Loading..." className="h-4 w-4" />
                  ) : (
                    "✓"
                  )}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isUpdatingLikeCount}
                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-700  text-white hover:bg-red-800 disabled:opacity-50"
                >
                  ✕
                </button>
              </div>
            ) : (
              <>
                <span className="text-sm text-white">{currentValue}</span>
                <AiFillEdit
                  className="cursor-pointer text-[#FF8000] hover:text-orange-600"
                  size={16}
                  onClick={() => handleEditLikeCount(rowId, currentValue)}
                />
              </>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("is_active", {
      id: "is_active",
      header: () => (
        <p className="w-[150px] text-sm font-bold uppercase text-white">
          Action
        </p>
      ),

      cell: (info) => {
        const rowId = info.row.original._id;
        const isAdmin = info.row.original.accountDetails?.is_admin;
        return (
          <div className="flex gap-3">
            <div
              className={`ms-center flex h-8 w-8 items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150 ${
                isAdmin
                  ? "cursor-pointer"
                  : "cursor-not-allowed opacity-50"
              }`}
              onClick={() => {
                if (isAdmin) {
                  handleAddOpen("update", info.row.original);
                }
              }}
            >
              <AiFillEdit className="" />
            </div>
            <div
              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg  bg-[#369FFF1A]/10 text-[#369FFF] duration-150 "
              onClick={() => handleOpen(info.row.original)}
            >
              <IoEye />
            </div>
            <div
              className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
              onClick={() =>
                handleOpenDelete(rowId, info.getValue(), info.row.original)
              }
            >
              {isViewLoading && rowId === id ? (
                <img
                  src={Loader}
                  alt="Loading..."
                  className="mx-auto h-4 w-4"
                />
              ) : (
                <MdDeleteForever size={20} />
              )}
            </div>
            {/* <div
              className={`ms-center m-0 flex h-8 w-28 cursor-default items-center justify-center ${
                info.getValue() ? "bg-green-500" : "bg-orange-500"
              } rounded-md px-1.5 text-sm font-bold text-white`}
            >
              {info.getValue() ? "Activated" : "Not Activated"}
            </div> */}
          </div>
        );
      },
    }),
  ]; // eslint-disable-next-line

  const handleAddOpen = (type, data) => {
    setIsOpen(true);
    document.body.style.overflow = "hidden";
    setEventData(data);
  };

  const handleOpen = async (item) => {
    setViewOpen(item);
    setId(item._id);
    document.body.style.overflow = "hidden";
  };

  // Initialize from cache if available
  const [data, setData] = useState(tabCache.exploreEvents.data || []);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  const getEvents = async (isSearching = false) => {
    const isFirstLoad = tabCache.exploreEvents.firstLoad;
    
    // Show loader on first load or when searching
    if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
      setIsLoading(true);
    }
    
    
    try {
      const eventType = searchParams.get("event_type");
      const filters = {
        typeid: 2,
      };
      
      // Add event_type filter if present in URL and not "futureAll"
      // "futureAll" means show all events, so no additional filter needed
      if (eventType && eventType !== "futureAll") {
        filters.event_type = eventType;
      }

      // Only add is_active filter if eventStatus is true (Active)
      // If eventStatus is "all", we'll send is_all: true in body instead
      if (eventStatus === true) {
        filters.is_active = eventStatus;
      }

      if (dateRangeFromParams) {
        filters.startDate = dateRangeFromParams.startDate;
        filters.endDate = dateRangeFromParams.endDate;
      }

      const body = {
        pagination: {
          page: page,
          pageSize: pageSize,
          projection: {},
          filters: filters,
          sort: {},
        },
        searchtext: encodeURIComponent(searchText),
        ...(dateRangeFromParams
          ? {
              startDate: dateRangeFromParams.startDate,
              endDate: dateRangeFromParams.endDate,
            }
          : {}),
        // Add is_all: true when "All" is selected
        ...(eventStatus === "all" ? { is_all: true } : {}),
      };

      const response = await EventService.getAllEvents(body);
      const eventsData = response.data.data;
      
      // Update state and cache
      setData(eventsData);
      tabCache.exploreEvents.data = eventsData;
      setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.exploreEvents.firstLoad = false;
      }
    } catch (error) {
      Toast("error", error.response.data.message);
      const emptyData = [];
      setData(emptyData);
      tabCache.exploreEvents.data = emptyData;
      if (isFirstLoad) {
        tabCache.exploreEvents.firstLoad = false;
      }
    } finally {
      if (isFirstLoad || isSearching || isPageChanging || isFilterChanging) {
        setIsLoading(false);
      }
      setIsPageChanging(false); // 👈 STOP pagination loader
      setIsFilterChanging(false);
    }
    
  };

  useEffect(() => {
    // Reset page to 1 when filters change
    setIsFilterChanging(true);
    setPage(1);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [eventStatus]);

  useEffect(() => {
    // Check if searchText changed (this indicates a search operation)
    const isSearchChange = prevSearchTextRef.current !== searchText;
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }
    
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.exploreEvents.data) {
      // Data already shown from initial state, just fetch in background
      // Show loader if this is a search operation
      getEvents(isSearchChange);
    } else {
      // No cached data, fetch and show loader
      getEvents(false);
    }
    // eslint-disable-next-line
  }, [page, pageSize, searchText, eventStatus, searchParams, dateRangeFromParams]);

  const handlePageChange = (newPage) => {
    setIsPageChanging(true);   // 👈 START loader for pagination
    setPage(newPage);
  };
  
  

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleClose = () => {
    setViewOpen(null);
    setId("");
    document.body.style.overflow = "unset";
  };

  const handleModalClose = () => {
    setIsOpen(false);
    document.body.style.overflow = "unset";
    setEventData([]);
  };

  const statusOptions = [
     { label: "All", value: "all" },
    { label: "Upcoming", value: true },
   
  ];

  const handleSelectEventStatus = (option) => {
    setIsFilterChanging(true);
    setEventStatus(option.value);
  };

  return (
    <>
      <style>
        {`
          @media (min-width: 500px) and (max-width: 639px) {
            .search-box-responsive {
              max-width: 140px !important;
            }
            .search-input-field-explore {
              width: 100px !important;
            }
          }
          @media (max-width: 639px) {
            .events-select-dropdown-explore .select__single-value {
              font-size: 0.90rem !important;
            }
            .events-select-dropdown-explore .select__control {
              font-size: 0.90rem !important;
            }
            .events-select-dropdown-explore .select__placeholder {
              font-size: 0.90rem !important;
            }
            .events-select-dropdown-explore .select__option {
              font-size: 0.90rem !important;
            }
            .add-event-button-responsive {
              font-size: 0.90rem !important;
            }
          }
          @media (min-width: 500px) and (max-width: 575px) {
            .search-container-responsive {
              flex-direction: row !important;
              align-items: center !important;
              justify-content: space-between !important;
              gap: 0.5rem !important;
            }
            .search-container-responsive > div:first-child {
              width: auto !important;
              flex-shrink: 1 !important;
            }
            .search-container-responsive .search-box-responsive {
              max-width: 130px !important;
              width: 130px !important;
              min-width: 130px !important;
            }
            .search-container-responsive .search-input-field-explore {
              max-width: 70px !important;
              width: 70px !important;
            }
            .search-container-responsive .buttons-container-responsive {
              width: auto !important;
              flex-shrink: 0 !important;
              justify-content: flex-end !important;
            }
            .search-container-responsive .events-select-dropdown-explore {
              width: 110px !important;
              min-width: 140px !important;
            }
            .search-container-responsive .add-event-button-responsive {
              max-width: 120px !important;
              flex-shrink: 0 !important;
            }
          }
        `}
      </style>
      <div className="mt-0.3 grid h-full grid-cols-1 gap-5">
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        {/* <ComplexTable
          columnsData={columnsDataComplex}
          tableData={tableDataComplex}
        /> */}

        <Card extra={"w-full h-full px-6 pb-6 overflow-x-auto"}>
          {/* <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-white">Users</div>
          </div> */}

          <div className="search-container-responsive mt-4 -ml-2 flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex w-full items-center sm:w-auto">
              <div className="search-box-responsive flex w-full items-center rounded-full bg-navy-900 py-3 text-white sm:w-[200px] xl:w-[180px]">
                <p className="pl-3 pr-2 text-xl">
                  <FiSearch className="h-4 w-4 text-white" />
                </p>
                <input
                  type="text"
                  placeholder="Search..."
                  className="search-input-field-explore block w-full bg-navy-900 text-sm font-medium max-w-[120px] text-white outline-none placeholder:!text-white sm:w-fit"
                  onChange={handleSearchInputChange}
                  value={searchInputValue}
                />
              </div>
            </div>
            <div className="buttons-container-responsive flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end -mr-4">
              <div className="flex items-center sm:w-auto ">
                <Select
                  className="default events-select-dropdown-explore w-full sm:w-[150px] !text-white"
                  classNamePrefix="select"
                  defaultValue={statusOptions[0]}
                  name="event-status"
                  options={statusOptions}
                  value={statusOptions.find(
                    (option) => option.value === eventStatus
                  ) || statusOptions[0]}
                  onChange={handleSelectEventStatus}
                  styles={colourStyles}
                />
              </div>
              <button
                onClick={() => {
                  handleAddOpen("add");
                }}
                className="add-event-button-responsive h-11 w-fit max-w-[120px] whitespace-nowrap rounded-full bg-orangePrimary px-3 text-sm text-white duration-150 hover:bg-orangePrimary/80 sm:max-w-none sm:px-4 sm:text-base"
              >
                Add Event
              </button>
            </div>
          </div>

          <div
            className={`table-scroll mt-4 -mx-6 relative ${
              isLoading
                ? "xxs:h-[40vh] xs:h-[47vh] sm:h-[60vh] overflow-hidden"
                : "overflow-x-auto"
            }`}
          >
            {isLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`w-full ${isLoading ? "table-fixed h-full" : ""}`}
              style={
                isLoading
                  ? { tableLayout: "fixed", width: "100%", height: "100%" }
                  : { tableLayout: "auto", minWidth: "100%" }
              }
            >
              <thead className={isLoading ? "hidden" : ""}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="!border-px !border-gray-400"
                  >
                    {headerGroup.headers.map((header) => {
                      const headerWidthClass =
                        header.column.id === "description"
                          ? "min-w-[220px]"
                          : "min-w-[150px]";
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          onClick={header.column.getToggleSortingHandler()}
                          className={`${headerWidthClass} cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start first:pl-6 last:pr-6`} 
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className={isLoading ? "h-full" : ""}>
                {isLoading ? (
                  <tr className="md:hidden h-full">
                    <td colSpan={columns.length} className="h-full">
                      <div className="flex h-full items-center justify-center">
                        <img src={Loader} alt="Loading..." className="h-16 w-16" />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => {
                    return (
                      <tr
                        key={row.id}
                        className="group border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer"
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const baseWidthClass =
                            cell.column.id === "description"
                              ? "min-w-[220px]"
                              : "min-w-[150px]";
                          const isFirst = cellIndex === 0;
                          const isLast = cellIndex === row.getVisibleCells().length - 1;
                          return (
                            <td
                              key={cell.id}
                              className={`${baseWidthClass} border-white/0 py-3 pr-4 text-left ${isFirst ? 'pl-6' : ''} ${isLast ? 'pr-6' : ''}`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      <p className="my-3">Data Not Found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {isOpen && (
            <CreateEventPosts
              type="event"
              isOpen={isOpen}
              onClose={handleModalClose}
              getEvents={getEvents}
              eventData={eventData}
            />
          )}

          {viewOpen && (
            <EventsModal
              isOpen={viewOpen}
              profileData={viewOpen}
              onClose={handleClose}
            />
          )}

          {table.getRowModel().rows.length > 0 && !isLoading && (
            <Pagination
              page={page}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}
          {popupOpenDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
              <div
                className="w-[85%] sm:w-96  rounded-xl bg-navy-800 p-6 shadow-lg"
                ref={activateRef}
              >
                <h2 className="mb-4 text-xl font-bold text-white">Delete</h2>
                {isSoldTickets ? (
  <div className="">
    <p className="text-sm font-semibold text-red-600">
      ⚠ Tickets are already sold for this event.
    </p>
    <p className="mt-1 text-sm mb-4 text-gray-600">
      Are you sure you want to delete it?
    </p>
  </div>
) : (
  <p className="mb-4 text-gray-600">
    Are you sure you want to delete this event?
  </p>
)}


                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setPopupOpenDelete(false);
                      document.body.style.overflow = "unset";
                    }}
                    className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleDelete();
                    }}
                    className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
                  >
                    {confirmLoader ? (
                      <img
                        src={Loader}
                        alt="Loading..."
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
    </>
  );
};

export default Events;
